import { useEffect, useRef, useState } from "react";
import { AlertTriangle } from "lucide-react";
import { http, errMsg } from "../../api";

export default function ExamRunner({ exam, onFinished }) {
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState(exam.answers || {});
  const [left, setLeft] = useState(() => Math.max(0, Math.floor((new Date(exam.deadline_at) - Date.now()) / 1000)));
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [warning, setWarning] = useState(null);
  const [error, setError] = useState("");
  const submitted = useRef(false);
  const lastViolation = useRef(0);
  const answersRef = useRef(answers);
  answersRef.current = answers;
  const path = `/creator/tests/language/${encodeURIComponent(exam.language)}`;

  const submit = async () => {
    if (submitted.current) return;
    submitted.current = true; setBusy(true); setConfirm(false);
    try { const { data } = await http.post(`${path}/submit`, { answers: answersRef.current }); onFinished(data); }
    catch (e) { submitted.current = false; setBusy(false); setError(errMsg(e)); }
  };

  useEffect(() => {
    const t = setInterval(() => setLeft(l => { if (l <= 1) { clearInterval(t); submit(); return 0; } return l - 1; }), 1000);
    return () => clearInterval(t);
  }, []);
  useEffect(() => {
    const t = setInterval(() => http.post(`${path}/save`, { answers: answersRef.current }).catch(() => {}), 20000);
    return () => clearInterval(t);
  }, []);
  useEffect(() => {
    // Anti-cheat: leaving the tab/window is reported; after the allowed warnings the server auto-submits.
    const report = async () => {
      if (submitted.current) return;
      const t = Date.now(); if (t - lastViolation.current < 2500) return; lastViolation.current = t;
      try {
        const { data } = await http.post(`${path}/violation`, { answers: answersRef.current });
        if (data.auto_submitted) { submitted.current = true; onFinished(data); } else setWarning(data);
      } catch { /* network hiccup: nothing to do */ }
    };
    const onVis = () => { if (document.hidden) report(); };
    document.addEventListener("visibilitychange", onVis); window.addEventListener("blur", report);
    return () => { document.removeEventListener("visibilitychange", onVis); window.removeEventListener("blur", report); };
  }, []);

  const q = exam.questions[idx];
  const total = exam.questions.length;
  const answered = Object.keys(answers).length;
  const mm = String(Math.floor(left / 60)).padStart(2, "0"), ss = String(left % 60).padStart(2, "0");

  return (
    <section className="exam" data-testid="exam-runner">
      <div className="exam-head">
        <div><small>{exam.language} · language &amp; comprehension test</small><strong>Question {idx + 1} of {total}</strong></div>
        <div className={`exam-timer ${left < 60 ? "danger" : ""}`} data-testid="exam-timer">{mm}:{ss}</div>
      </div>
      <div className="exam-rule" data-testid="exam-rule"><AlertTriangle size={13} /> Do not switch tabs or windows. You get {exam.max_tab_switch_warnings} warning(s); the next switch auto-submits the test.{exam.violations > 0 && ` Warnings used: ${exam.violations}.`}</div>
      <div className="progress-track"><div className="progress-fill" style={{ width: `${((idx + 1) / total) * 100}%` }} /></div>
      <div className="exam-question" data-testid={`exam-question-${q.id}`}>
        {q.section && <span className="badge">{q.section}</span>}
        <h3>{q.question}</h3>
        <div className="exam-options">
          {q.options.map((o, i) => (
            <button type="button" key={i} className={`option-btn ${answers[q.id] === i ? "selected" : ""}`} onClick={() => setAnswers({ ...answers, [q.id]: i })} data-testid={`exam-option-${i}`}>
              <span className="opt-letter">{String.fromCharCode(65 + i)}</span>{o}
            </button>
          ))}
        </div>
      </div>
      <div className="exam-nav">
        <button className="ghost" disabled={idx === 0} onClick={() => setIdx(idx - 1)} data-testid="exam-prev">Previous</button>
        <span data-testid="exam-answered-count">{answered}/{total} answered</span>
        {idx < total - 1
          ? <button className="primary" onClick={() => setIdx(idx + 1)} data-testid="exam-next">Next</button>
          : <button className="primary" onClick={() => setConfirm(true)} disabled={busy} data-testid="exam-submit">{busy ? "Submitting…" : "Submit test"}</button>}
      </div>
      {error && <div className="notice error" data-testid="exam-error">{error}</div>}
      <div className="exam-grid">
        {exam.questions.map((qq, i) => (
          <button type="button" key={qq.id} className={`q-dot ${answers[qq.id] !== undefined ? "answered" : ""} ${i === idx ? "current" : ""}`} onClick={() => setIdx(i)} data-testid={`exam-jump-${i + 1}`}>{i + 1}</button>
        ))}
      </div>
      {warning && (
        <div className="modal-backdrop">
          <div className="modal warning-modal" data-testid="tab-switch-warning">
            <AlertTriangle size={34} color="var(--warning)" />
            <h2>Warning {warning.violations} of {warning.max_warnings}</h2>
            <p>You left the test window. Switching tabs, apps or windows is not allowed during the test.{warning.remaining > 1 ? ` You have ${warning.remaining - 1} warning(s) left.` : " The next switch will submit your test automatically."}</p>
            <button className="primary" onClick={() => setWarning(null)} data-testid="tab-switch-ack">I understand — continue test</button>
          </div>
        </div>
      )}
      {confirm && (
        <div className="modal-backdrop" onClick={() => setConfirm(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} data-testid="exam-confirm-modal">
            <h2>Submit your test?</h2>
            <p>You answered {answered} of {total} questions. Answers cannot be changed after submitting.</p>
            <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
              <button className="primary" onClick={submit} disabled={busy} data-testid="exam-confirm-submit">Yes, submit</button>
              <button className="ghost" onClick={() => setConfirm(false)} data-testid="exam-cancel-submit">Keep answering</button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
